import React, { useState, useEffect } from "react";
import {
  Button,
  Input,
  Form,
  Row,
  Col,
  Select,
  Radio,
  Checkbox,
  Typography,
} from "antd";
import { Link, useLocation } from "react-router-dom";
import "./index.css";
import RemitClickInfo from "../Components/RemitClickInfo";
import PrivacyPolicyModal from "../Components/PrivacyPolicyModal";
import TermsConditionsModal from "../Components/TermsConditionsModal";
import { CloseOutlined } from "@ant-design/icons";
import { signUpUser } from "../Services/authServices";
import { confirmSignUp, resendSignUpCode } from "aws-amplify/auth";
import { signIn, fetchUserAttributes } from "@aws-amplify/auth";
import logo from "../Image/logo.png";
import { signedPost } from "../Services/awsSignedFetch";
import { useNavigate } from "react-router-dom";

const OTP_RECOVERY_PASSWORD_KEY = "bajcan_otp_recovery_password";

const SignUp = ({ onLoginSuccess }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const isMobile = viewportWidth < 768;
  const { Text } = Typography;
  const [form] = Form.useForm();
  const selectedAccountType = Form.useWatch("accountType", form);
  const [isOtpStage, setIsOtpStage] = useState(false);
  const [username, setUsername] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isError, setIsError] = useState(false);
  const [isBusiness, setIsBusiness] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [phonerrMsg, setPhoneErrMsg] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const normalizeEmail = (email) => (email || "").trim().toLowerCase();
  const getErrorMessage = (error, fallbackMessage) => {
    const rawMessage =
      error?.message ||
      error?.errors?.[0]?.message ||
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "";
    const message = String(rawMessage || error || "")
      .split(":")
      .pop()
      .trim();
    return message || fallbackMessage;
  };

  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);
  const passwordValue = Form.useWatch("password", form);
  const confirmPasswordValue = Form.useWatch("cpassword", form);
  const isConfirmPasswordMatched =
    Boolean(confirmPasswordValue) && passwordValue === confirmPasswordValue;

  // Function to validate Canadian phone number
  const validateCanadianPhoneNumber = (phoneNumber) => {
    const cleanedPhoneNumber = phoneNumber.replace(/\D/g, "");
    const canadianPhoneRegex = /^(1)?\d{10}$/;
    return canadianPhoneRegex.test(cleanedPhoneNumber);
  };

  const formatPhoneNumber = (rawValue, selectedCountryCode = countryCode) => {
    const digitsOnly = (rawValue || "").replace(/\D/g, "");

    if (selectedCountryCode === "+1246") {
      const digits = digitsOnly.slice(0, 7);
      if (digits.length <= 3) {
        return digits;
      }
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }

    const digits = digitsOnly.slice(0, 10);
    if (digits.length === 0) {
      return "";
    }
    if (digits.length <= 3) {
      return `(${digits}`;
    }
    if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleCountryCodeChange = (value) => {
    setCountryCode(value);
    const currentPhoneValue = form.getFieldValue("phone_number") || "";
    form.setFieldsValue({
      phone_number: formatPhoneNumber(currentPhoneValue, value),
    });
    setIsValid(true);
  };

  const handlePhoneNumberChange = (event) => {
    const formattedPhone = formatPhoneNumber(event?.target?.value || "");
    form.setFieldsValue({ phone_number: formattedPhone });
    setPhoneNumber(formattedPhone);
    setIsValid(true);
    setPhoneErrMsg("");
  };
  // signup function is here
  async function handleSignUp(values) {
    if (isSigningUp) {
      return;
    }

    setIsSigningUp(true);
    setIsError(false);
    setErrMsg("");
    setPhoneErrMsg("");

    let isValidPhoneNumber = false;

    if (countryCode === "+1246") {
      const cleanedPhoneNumber = values.phone_number.replace(/\D/g, "");
      const barbadosPhoneRegex = /^(\+1246)?[ -]?[0-9]{7}$/;
      isValidPhoneNumber = barbadosPhoneRegex.test(cleanedPhoneNumber);
      if (!isValidPhoneNumber) {
        setPhoneErrMsg("Please enter a valid phone number.");
      }
    } else if (countryCode === "+1") {
      isValidPhoneNumber = validateCanadianPhoneNumber(values.phone_number);
      setPhoneErrMsg("Please enter a valid phone number.");
    }

    setIsValid(isValidPhoneNumber);

    const fullPhoneNumber = `${countryCode}${values.phone_number.replace(/\D/g, "")}`;
    setPhoneNumber(fullPhoneNumber);

    if (!isValidPhoneNumber) {
      setIsValid(false);
      setIsSigningUp(false);
      return;
    }

    if (form.getFieldValue("accountType") === "business") {
      setIsBusiness(true);
    } else {
      setIsBusiness(false);
    }

    try {
      const normalizedEmail = normalizeEmail(values.email);

      const isBarbadosWallet = countryCode === "+1246";
      const country = isBarbadosWallet ? "Barbados" : "Canada";
      const walletCurrency = isBarbadosWallet ? "BBD" : "CAD";

      await signUpUser({
        username: normalizedEmail,
        password: values.password,
        email: normalizedEmail,
        name: values.name,
        phone_number: fullPhoneNumber,
        accountType: values.accountType,
        businessName:
          values.accountType === "business" ? values.business_name || "" : "",
        country,
        walletCurrency,
      });

      setUsername(normalizedEmail);

      form.setFieldsValue({
        email: normalizedEmail,
      });

      setIsOtpStage(true);
      setResendCountdown(60);
    } catch (error) {
      setIsError(true);
      const message = getErrorMessage(
        error,
        "Unable to create your account right now.",
      );
      setErrMsg(message);
      console.log("Error", message);
    } finally {
      setIsSigningUp(false);
    }
  }

  useEffect(() => {
    const handleWindowResize = () => {
      setViewportWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  useEffect(() => {
    const state = location.state;
    if (!state?.verifyOtp || !state?.username) {
      return;
    }

    const normalizedEmail = normalizeEmail(state.username);

    setUsername(normalizedEmail);
    setIsOtpStage(true);
    setResendCountdown(state.otpAlreadySent ? 60 : 0);
    setIsError(false);
    setErrMsg("");

    form.setFieldsValue({
      email: normalizedEmail,
      password:
        state.password || sessionStorage.getItem(OTP_RECOVERY_PASSWORD_KEY) || "",
    });
  }, [location.state, form]);

  useEffect(() => {
    if (!isOtpStage || resendCountdown <= 0) {
      return undefined;
    }

    const countdownTimer = setInterval(() => {
      setResendCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(countdownTimer);
  }, [isOtpStage, resendCountdown]);

  const handleResendOtp = async () => {
    if (!username || resendCountdown > 0 || isResendingOtp) {
      return;
    }

    try {
      setIsResendingOtp(true);
      setIsError(false);
      setErrMsg("");
      await resendSignUpCode({ username });
      setResendCountdown(60);
    } catch (error) {
      setIsError(true);
      const message = getErrorMessage(
        error,
        "Unable to resend the OTP right now.",
      );
      setErrMsg(message);
      setResendCountdown(0);
    } finally {
      setIsResendingOtp(false);
    }
  };

  const handleOtpSubmit = async (values) => {
    if (isVerifyingOtp) {
      return;
    }

    const { otp } = values;
    setIsVerifyingOtp(true);
    setIsError(false);
    setErrMsg("");

    try {
      const normalizedUsername = normalizeEmail(username);
      const normalizedFormEmail = normalizeEmail(form.getFieldValue("email"));

      await confirmSignUp({
        username: normalizedUsername,
        confirmationCode: otp,
      });

      await signIn({
        username: normalizedUsername,
        password: form.getFieldValue("password"),
      });
      sessionStorage.removeItem(OTP_RECOVERY_PASSWORD_KEY);

      const userAttributes = await fetchUserAttributes();

      const fallbackEmail = normalizeEmail(
        userAttributes.email || normalizedFormEmail || normalizedUsername || "",
      );

      const accountTypeAttr =
        userAttributes["custom:accountType"] ||
        form.getFieldValue("accountType") ||
        "personal";

      const businessNameAttr =
        userAttributes["custom:businessName"] ||
        form.getFieldValue("business_name") ||
        "";

      const fallbackName =
        userAttributes.name ||
        form.getFieldValue("name") ||
        businessNameAttr ||
        (fallbackEmail ? fallbackEmail.split("@")[0] : "User");

      const fallbackPhone = userAttributes.phone_number || phoneNumber || "";

      const countryAttr =
        userAttributes["custom:country"] ||
        (countryCode === "+1246" ? "Barbados" : "Canada");

      const walletCurrencyAttr =
        userAttributes["custom:walletCurrency"] ||
        (countryAttr === "Barbados" ? "BBD" : "CAD");

      const isBusinessUser = accountTypeAttr === "business" || isBusiness;

      const existingTableID = userAttributes["custom:TableID"] || null;

      await createUserSetupFunction({
        userID: existingTableID || undefined,
        name: fallbackName,
        email: fallbackEmail,
        phoneNumber: fallbackPhone,
        accountType: accountTypeAttr,
        isOrganization: isBusinessUser,
        businessName: isBusinessUser ? businessNameAttr : "",
        country: countryAttr,
        currency: walletCurrencyAttr,
        termsAndConditionsAccepted: true,
        privacyPolicyAcknowledged: true,
      });

      onLoginSuccess();
    } catch (error) {
      const normalizedMessage = getErrorMessage(error, "");

      if (
        error?.name === "NotAuthorizedException" ||
        normalizedMessage.toLowerCase().includes("incorrect username or password")
      ) {
        navigate("/forgetPassword", {
          state: {
            email: normalizeEmail(username),
            message:
              "Your account is verified. Reset your password to continue.",
            sendResetCode: true,
            autoSignInAfterReset: true,
          },
        });
        return;
      }

      console.error("Error verifying OTP", error);
      setIsError(true);
      const message = getErrorMessage(
        error,
        "Unable to verify OTP or finish account setup.",
      );
      console.log(message);
      setErrMsg(message);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const createUserSetupFunction = async (setupPayload) => {
    console.log("user setup payload.....", setupPayload);
    try {
      const { lambdaStatus, data } = await signedPost(
        "https://8dxbdu1udf.execute-api.us-east-2.amazonaws.com/default/createUserDefaultMarkup-staging",
        setupPayload,
      );
      if (lambdaStatus < 200 || lambdaStatus >= 300) {
        throw new Error(data?.error || "User setup request failed.");
      }
      console.log("user setup completed", data);
    } catch (error) {
      console.log("setup error", error);
      throw error;
    }
  };
  return (
    <>
      {isMobile ? (
        <div className="signin-mobile-wrap">
          <Row
            className="signin-mobile-header"
            style={{
              display: "flex",
              borderBottomStyle: "solid",
              borderBottomColor: "#F2F2F2",
              borderBottomWidth: "0.1px",
              flexShrink: 0,
            }}
          >
            <Col
              span={20}
              style={{
                display: "flex",
                paddingTop: 10,
                paddingBottom: 10,
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: "1rem",
                }}
              >
                <img
                  src={logo}
                  alt="BajCan"
                  className="headerLogo headerLogo--mobile"
                />
              </div>
            </Col>
            <Col
              span={4}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Link to="/">
                <Button
                  type="primary"
                  icon={<CloseOutlined />}
                  className="crossSign-mobile"
                />
              </Link>
            </Col>
          </Row>
          <Row
            className="signin-row--mobile"
            style={{
              flex: 1,
              minHeight: 0,
              height: "auto",
              overflow: "auto",
            }}
          >
            <Col xs={{ span: 24 }}>
              <div className="signinRightSec">
                <div className="signin-mobile-welcome">
                  <p className="signin-mobile-tagline">
                    {isOtpStage ? "Verify your email" : "Create your account"}
                  </p>
                  <p className="signin-mobile-subtitle">
                    {isOtpStage
                      ? "Enter the 6-digit code sent to your email"
                      : "Join BajCan to send money securely"}
                  </p>
                </div>
                {!isOtpStage ? (
                  <div className="signbox signbox--mobile-signin">
                    <h2 className="signupTitle">Sign Up</h2>
                    <div className="form">
                      <Form
                        form={form}
                        initialValues={{ accountType: "personal" }}
                        validateTrigger="onSubmit"
                        onFinish={handleSignUp}
                      >
                        <Form.Item
                          name="accountType"
                          rules={[
                            {
                              required: true,
                              message: "Please select your account type!",
                            },
                          ]}
                        >
                          <Radio.Group
                            className="accountTypeGroup"
                            buttonStyle="solid"
                          >
                            <Radio.Button value="personal">
                              Personal
                            </Radio.Button>
                            <Radio.Button value="business">
                              Business
                            </Radio.Button>
                          </Radio.Group>
                        </Form.Item>

                        {selectedAccountType === "business" && (
                          <>
                            <label
                              className="formlabel"
                              style={{ fontSize: "16px" }}
                            >
                              Business Name
                            </label>
                            <Form.Item
                              name="business_name"
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter your business name!",
                                },
                              ]}
                            >
                              <Input
                                className="forminput"
                                placeholder="Business Name"
                              />
                            </Form.Item>
                          </>
                        )}

                        <label
                          className="formlabel"
                          style={{ fontSize: "16px" }}
                        >
                          Name
                        </label>
                        <Form.Item
                          name="name"
                          rules={[
                            {
                              required: true,
                              message: "Please enter your first name",
                            },
                          ]}
                        >
                          <Input
                            className="forminput"
                            placeholder="Name"
                            autoComplete="off"
                          />
                        </Form.Item>

                        <label
                          className="formlabel"
                          style={{ fontSize: "16px" }}
                        >
                          Email
                        </label>
                        <Form.Item
                          name="email"
                          rules={[
                            {
                              required: true,
                              message: "Please input your email address!",
                            },
                            {
                              type: "email",
                              message: "The input is not valid E-mail!",
                            },
                          ]}
                        >
                          <Input
                            className="forminput"
                            placeholder="Email"
                            onChange={(e) => {
                              form.setFieldsValue({
                                email: normalizeEmail(e.target.value),
                              });
                            }}
                          />
                        </Form.Item>

                        <label
                          className="formlabel"
                          style={{ fontSize: "16px" }}
                        >
                          Phone Number
                        </label>

                        <Form.Item
                          name="phone_number"
                          rules={[
                            {
                              required: true,
                              message: "Please input your phone number!",
                            },
                            () => ({
                              validator(_, value) {
                                if (!value || /^[\d()\-\s]+$/.test(value)) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(
                                  new Error(
                                    "Please enter a valid phone number.",
                                  ),
                                );
                              },
                            }),
                          ]}
                        >
                          <Input
                            className="forminput"
                            style={{ padding: 0 }}
                            onChange={handlePhoneNumberChange}
                            maxLength={countryCode === "+1246" ? 8 : 14}
                            size="large"
                            placeholder={
                              countryCode === "+1246"
                                ? "XXX-XXXX"
                                : "(XXX) XXX-XXXX"
                            }
                            addonBefore={
                              <Select
                                style={{ width: 100 }}
                                value={countryCode}
                                onChange={handleCountryCodeChange}
                              >
                                <Select.Option value="+1">+1</Select.Option>
                                <Select.Option value="+1246">
                                  +1246
                                </Select.Option>
                              </Select>
                            }
                          />
                        </Form.Item>
                        {!isValid && (
                          <p style={{ color: "red" }}>{phonerrMsg}</p>
                        )}

                        <label
                          className="formlabel"
                          style={{ fontSize: "16px" }}
                        >
                          Password
                        </label>
                        <Form.Item
                          name="password"
                          rules={[
                            {
                              required: true,
                              message: "Please input your password!",
                            },
                          ]}
                        >
                          <Input.Password
                            className="forminput"
                            placeholder="Password"
                          />
                        </Form.Item>

                        <label
                          className="formlabel"
                          style={{ fontSize: "16px" }}
                        >
                          Confirm Password
                        </label>
                        <Form.Item
                          name="cpassword"
                          dependencies={["password"]}
                          hasFeedback={isConfirmPasswordMatched}
                          rules={[
                            {
                              required: true,
                              message: "Please confirm your password!",
                            },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (
                                  !value ||
                                  getFieldValue("password") === value
                                ) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(
                                  new Error(
                                    "The two passwords that you entered do not match!",
                                  ),
                                );
                              },
                            }),
                          ]}
                        >
                          <Input.Password
                            className="forminput"
                            placeholder="Confirm Password"
                          />
                        </Form.Item>

                        <Form.Item
                          name="agreement" // optional: if you want Form validation
                          valuePropName="checked"
                          rules={[
                            {
                              validator: (_, value) =>
                                value
                                  ? Promise.resolve()
                                  : Promise.reject(
                                      new Error("You must agree to continue"),
                                    ),
                            },
                          ]}
                        >
                          <Checkbox
                            onChange={(e) => setHasAgreed(e.target.checked)}
                          >
                            I have read the{" "}
                            <a
                              className="agreement-link"
                              onClick={(e) => {
                                e.preventDefault(); // prevent any default behavior
                                setIsPrivacyOpen(true);
                              }}
                              style={{
                                color: "#1890ff",
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                            >
                              Privacy Policy
                            </a>{" "}
                            and{" "}
                            <a
                              className="agreement-link"
                              onClick={(e) => {
                                e.preventDefault();
                                setIsTermsOpen(true);
                              }}
                              style={{
                                color: "#1890ff",
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                            >
                              Terms & Conditions
                            </a>
                          </Checkbox>
                        </Form.Item>
                        {isError && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              marginBottom: "1rem",
                            }}
                          >
                            <Text style={{ color: "red" }}>{errMsg}</Text>
                          </div>
                        )}
                        <Button
                          type="primary"
                          htmlType="submit"
                          className="signupbtn"
                          disabled={!hasAgreed || isSigningUp}
                          loading={isSigningUp}
                        >
                          Sign Up
                        </Button>
                      </Form>
                      <PrivacyPolicyModal
                        open={isPrivacyOpen}
                        onClose={() => setIsPrivacyOpen(false)}
                      />
                      <TermsConditionsModal
                        open={isTermsOpen}
                        onClose={() => setIsTermsOpen(false)}
                      />
                      <p className="signupFooter">
                        Already have an Account?
                        <Link to="/signIn" style={{ textDecoration: "none" }}>
                          Sign In
                        </Link>
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="signbox signbox--mobile-signin">
                    <h2 className="signupTitle">Verify OTP</h2>
                    <Form
                      className="form"
                      onFinish={handleOtpSubmit}
                      validateTrigger="onSubmit"
                    >
                      {username ? (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            marginBottom: "0.75rem",
                          }}
                        >
                          <Text style={{ color: "#325FD5" }}>
                            {`We sent a verification code to ${username}.`}
                          </Text>
                        </div>
                      ) : null}
                      <label className="formlabel" style={{ fontSize: "16px" }}>
                        Enter OTP
                      </label>
                        <Form.Item
                          name="otp"
                          rules={[
                            {
                              required: true,
                              message: "Please enter the OTP sent to you",
                            },
                            {
                              pattern: /^\d{6}$/,
                              message: "Please enter a valid 6-digit OTP",
                            },
                          ]}
                        >
                        <Input
                          className="forminput"
                          placeholder="6-digit OTP"
                          maxLength={6}
                          disabled={isVerifyingOtp}
                        />
                      </Form.Item>
                      <Button
                        type="primary"
                        htmlType="submit"
                        className="signupbtn"
                        loading={isVerifyingOtp}
                        disabled={isVerifyingOtp}
                      >
                        Verify OTP
                      </Button>
                      {isError && (
                        <div style={{ marginTop: 10 }}>
                          <Text style={{ color: "red" }}>{errMsg}</Text>
                        </div>
                      )}
                      <Button
                        block
                        style={{ marginTop: 8 }}
                        onClick={handleResendOtp}
                        disabled={
                          resendCountdown > 0 ||
                          isResendingOtp ||
                          isVerifyingOtp
                        }
                      >
                        {isResendingOtp
                          ? "Resending OTP..."
                          : resendCountdown > 0
                            ? `Resend OTP in ${resendCountdown}s`
                            : "Resend OTP"}
                      </Button>
                    </Form>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>
      ) : (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Row style={{ flex: 1, alignItems: "stretch" }}>
            <Col xs={{ span: 0 }} md={{ span: 12 }} lg={{ span: 10 }}>
              <RemitClickInfo />
            </Col>
            <Col
              xs={{ span: 24 }}
              md={{ span: 12 }}
              lg={{ span: 14 }}
              style={{ display: "flex" }}
            >
              <div
                className="signUpRightSec"
                style={{ flex: 1, width: "100%" }}
              >
                {!isOtpStage ? (
                  <div className="signbox">
                    <Link
                      to="/"
                      style={{
                        display: "flex",
                        width: "100%",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        type="primary"
                        icon={<CloseOutlined />}
                        className="crossSign"
                      />
                    </Link>
                    <h2 className="signupTitle">Sign Up</h2>
                    <div className="form">
                      <Form
                        form={form}
                        initialValues={{ accountType: "personal" }}
                        validateTrigger="onSubmit"
                        onFinish={handleSignUp}
                      >
                        <Form.Item
                          name="accountType"
                          rules={[
                            {
                              required: true,
                              message: "Please select your account type!",
                            },
                          ]}
                        >
                          <Radio.Group
                            className="accountTypeGroup"
                            buttonStyle="solid"
                          >
                            <Radio.Button value="personal">
                              Personal
                            </Radio.Button>
                            <Radio.Button value="business">
                              Business
                            </Radio.Button>
                          </Radio.Group>
                        </Form.Item>
                        {selectedAccountType === "business" && (
                          <>
                            <label
                              className="formlabel"
                              style={{ fontSize: "16px" }}
                            >
                              Business Name
                            </label>
                            <Form.Item
                              name="business_name"
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter your business name!",
                                },
                              ]}
                            >
                              <Input
                                className="forminput"
                                placeholder="Business Name"
                              />
                            </Form.Item>
                          </>
                        )}
                        <label
                          className="formlabel"
                          style={{ fontSize: "16px" }}
                        >
                          Name
                        </label>
                        <Form.Item
                          name="name"
                          rules={[
                            {
                              required: true,
                              message: "Please enter your first name",
                            },
                          ]}
                        >
                          <Input
                            className="forminput"
                            placeholder="Name"
                            autoComplete="off"
                          />
                        </Form.Item>
                        <label
                          className="formlabel"
                          style={{ fontSize: "16px" }}
                        >
                          Email
                        </label>
                        <Form.Item
                          name="email"
                          rules={[
                            {
                              required: true,
                              message: "Please input your email address!",
                            },
                            {
                              type: "email",
                              message: "The input is not valid E-mail!",
                            },
                          ]}
                        >
                          <Input
                            className="forminput"
                            placeholder="Email"
                            onChange={(e) => {
                              form.setFieldsValue({
                                email: normalizeEmail(e.target.value),
                              });
                            }}
                          />
                        </Form.Item>
                        <label
                          className="formlabel"
                          style={{ fontSize: "16px" }}
                        >
                          Phone Number
                        </label>
                        <Form.Item
                          name="phone_number"
                          rules={[
                            {
                              required: true,
                              message: "Please input your phone number!",
                            },
                            () => ({
                              validator(_, value) {
                                if (!value || /^[\d()\-\s]+$/.test(value))
                                  return Promise.resolve();
                                return Promise.reject(
                                  new Error(
                                    "Please enter a valid phone number.",
                                  ),
                                );
                              },
                            }),
                          ]}
                        >
                          <Input
                            className="forminput"
                            onChange={handlePhoneNumberChange}
                            maxLength={countryCode === "+1246" ? 8 : 14}
                            placeholder={
                              countryCode === "+1246"
                                ? "XXX-XXXX"
                                : "(XXX) XXX-XXXX"
                            }
                            addonBefore={
                              <Select
                                style={{ width: 100 }}
                                value={countryCode}
                                onChange={handleCountryCodeChange}
                              >
                                <Select.Option value="+1">+1</Select.Option>
                                <Select.Option value="+1246">
                                  +1246
                                </Select.Option>
                              </Select>
                            }
                          />
                        </Form.Item>
                        {!isValid && (
                          <p style={{ color: "red" }}>{phonerrMsg}</p>
                        )}
                        <label
                          className="formlabel"
                          style={{ fontSize: "16px" }}
                        >
                          Password
                        </label>
                        <Form.Item
                          name="password"
                          rules={[
                            {
                              required: true,
                              message: "Please input your password!",
                            },
                          ]}
                        >
                          <Input.Password
                            className="forminput"
                            placeholder="Password"
                          />
                        </Form.Item>
                        <label
                          className="formlabel"
                          style={{ fontSize: "16px" }}
                        >
                          Confirm Password
                        </label>
                        <Form.Item
                          name="cpassword"
                          dependencies={["password"]}
                          hasFeedback={isConfirmPasswordMatched}
                          rules={[
                            {
                              required: true,
                              message: "Please confirm your password!",
                            },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (
                                  !value ||
                                  getFieldValue("password") === value
                                )
                                  return Promise.resolve();
                                return Promise.reject(
                                  new Error(
                                    "The two passwords that you entered do not match!",
                                  ),
                                );
                              },
                            }),
                          ]}
                        >
                          <Input.Password
                            className="forminput"
                            placeholder="Confirm Password"
                          />
                        </Form.Item>
                        <Form.Item
                          name="agreement"
                          valuePropName="checked"
                          rules={[
                            {
                              validator: (_, value) =>
                                value
                                  ? Promise.resolve()
                                  : Promise.reject(
                                      new Error("You must agree to continue"),
                                    ),
                            },
                          ]}
                        >
                          <Checkbox
                            onChange={(e) => setHasAgreed(e.target.checked)}
                          >
                            I have read the{" "}
                            <a
                              className="agreement-link"
                              onClick={(e) => {
                                e.preventDefault();
                                setIsPrivacyOpen(true);
                              }}
                              style={{
                                color: "#1890ff",
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                            >
                              Privacy Policy
                            </a>{" "}
                            and{" "}
                            <a
                              className="agreement-link"
                              onClick={(e) => {
                                e.preventDefault();
                                setIsTermsOpen(true);
                              }}
                              style={{
                                color: "#1890ff",
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                            >
                              Terms & Conditions
                            </a>
                          </Checkbox>
                        </Form.Item>
                        {isError && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              marginBottom: "1rem",
                            }}
                          >
                            <Text style={{ color: "red" }}>{errMsg}</Text>
                          </div>
                        )}
                        <Button
                          type="primary"
                          htmlType="submit"
                          className="signupbtn"
                          disabled={!hasAgreed || isSigningUp}
                          loading={isSigningUp}
                        >
                          Sign Up
                        </Button>
                      </Form>
                      <PrivacyPolicyModal
                        open={isPrivacyOpen}
                        onClose={() => setIsPrivacyOpen(false)}
                      />
                      <TermsConditionsModal
                        open={isTermsOpen}
                        onClose={() => setIsTermsOpen(false)}
                      />
                      <p className="signupFooter">
                        Already have an Account?{" "}
                        <Link to="/signIn" style={{ textDecoration: "none" }}>
                          Sign In
                        </Link>
                      </p>
                    </div>
                  </div>
                ) : (
                  <div
                    className="signbox signbox--otp"
                    style={{ width: "100%" }}
                  >
                    <div style={{ width: "100%" }}>
                      <h2 className="signupTitle">Verify OTP</h2>
                      <Form
                        className="form"
                        onFinish={handleOtpSubmit}
                        validateTrigger="onSubmit"
                      >
                        {username ? (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              marginBottom: "0.75rem",
                            }}
                          >
                            <Text style={{ color: "#325FD5" }}>
                              {`We sent a verification code to ${username}.`}
                            </Text>
                          </div>
                        ) : null}
                        <label
                          className="formlabel"
                          style={{ fontSize: "16px" }}
                        >
                          Enter OTP
                        </label>
                        <Form.Item
                          name="otp"
                          rules={[
                            {
                              required: true,
                              message: "Please enter the OTP sent to you",
                            },
                            {
                              pattern: /^\d{6}$/,
                              message: "Please enter a valid 6-digit OTP",
                            },
                          ]}
                        >
                          <Input
                            className="forminput"
                            placeholder="6-digit OTP"
                            maxLength={6}
                            disabled={isVerifyingOtp}
                          />
                        </Form.Item>
                        <Button
                          type="primary"
                          htmlType="submit"
                          className="signupbtn"
                          loading={isVerifyingOtp}
                          disabled={isVerifyingOtp}
                        >
                          Verify OTP
                        </Button>
                        {isError && (
                          <div style={{ marginTop: 10 }}>
                            <Text style={{ color: "red" }}>{errMsg}</Text>
                          </div>
                        )}
                        <Button
                          block
                          style={{ marginTop: 8 }}
                          onClick={handleResendOtp}
                          disabled={
                            resendCountdown > 0 ||
                            isResendingOtp ||
                            isVerifyingOtp
                          }
                        >
                          {isResendingOtp
                            ? "Resending OTP..."
                            : resendCountdown > 0
                              ? `Resend OTP in ${resendCountdown}s`
                              : "Resend OTP"}
                        </Button>
                      </Form>
                    </div>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>
      )}
    </>
  );
};
export default SignUp;
